import {
  AsyncCallArgs,
  fillNonNullValues,
  GridContainer,
  GridItem,
  handleFormSubmission,
  handleSubmitValidationFail,
  isNotEmpty,
  SubmitFormButton,
  useAsyncCall,
  useReactHookForm,
  useScroll,
  ValidationErrors,
} from "@duosoftbg/nacid-components";
import { Typography } from "@mui/material";
import { FormProvider } from "react-hook-form";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserUpdatableDetails } from "../../../../../../types/userTypes";
import { createBaseUserValidationSchema } from "../../../../../../yup/schema/users/userValidationSchema";
import { updateUserMainData } from "../../../../../../axios/api/services";
import PersonFormSection from "../../common/person/PersonFormSection";
import RepresentativeFormSection from "../../common/representative/RepresentativeFormSection";
import ContactAddressFormSection from "../../common/address/ContactAddressFormSection";
import ResultReceiveFormSection from "../../common/resultReceive/ResultReceiveFormSection";
import ReceiverAddressFormSection from "../../common/address/ReceiverAddressFormSection";
import { userDataUpdateInitialValues } from "../../../../../../init/userDataUpdateInitialValues";
import AccountDataSection from "./AccountDataSection";

const UserDataSections = ({ user }) => {
  const { t } = useTranslation();
  const { scrollToTop } = useScroll();
  const { asyncCall } = useAsyncCall();

  const { methods } = useReactHookForm<UserUpdatableDetails>({
    defaultValues: userDataUpdateInitialValues,
    validationSchema: createBaseUserValidationSchema,
  });

  useEffect(() => {
    if (user) {
      const updatedUser = { ...userDataUpdateInitialValues };
      fillNonNullValues(user, updatedUser);
      methods.reset(updatedUser);
    }
  }, [user, methods]);

  const onSubmit = (values) => {
    const mainUserDataUpdate: AsyncCallArgs = {
      promise: updateUserMainData(values),
      reactHooksForm: { methods },
      onSuccess: (response) => {
        if (isNotEmpty(response.personalNacidId)) {
          methods.setValue("personalNacidId", response.personalNacidId);
        } else {
          methods.setValue("personalNacidId", "");
        }
        toast.success(t("m.user.main.data.update.success"));
        scrollToTop(true);
      },
      onValidationError: () => {
        scrollToTop(true);
      },
    };
    asyncCall(mainUserDataUpdate);
  };

  const onYupValidationFail = () => {
    scrollToTop(true);
    handleSubmitValidationFail(methods.formState, t, undefined);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleFormSubmission({ methods, onSubmitFn: onSubmit, onValidationFail: onYupValidationFail, t })}
      >
        <ValidationErrors />
        <AccountDataSection user={user} />
        <PersonFormSection />
        <RepresentativeFormSection />
        <ContactAddressFormSection />
        <ResultReceiveFormSection />
        <ReceiverAddressFormSection />

        <GridContainer spacing={5} mt={0}>
          <GridItem sm={12} md={12}>
            <Typography align={"right"}>
              <SubmitFormButton label={"l.btn.save"} />
            </Typography>
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

export default UserDataSections;
